import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import { FilterField } from "../../../smartsetter-utils/filters-dialog/data"
import SchemaFilterBody, {
  schemaFilterBody,
} from "../../../smartsetter-utils/filters-dialog/filter-bodies/SchemaFilterBody"
import { fieldLabel } from "../../../smartsetter-utils/filters-dialog/filterSchema"
import { CALL_DISPOSITIONS } from "../calling/callDispositions"
import { TYPE_FILTER_OPTIONS } from "../candidates/candidateType"
import { AGENT_ROLES } from "../my-mls/RoleChip"
import { selectCRMSettings } from "../reducer"
import { useLoadCRMStages } from "../utils"
import { POTENTIAL_TIER_FILTER_OPTIONS } from "./potentialTierConstants"

/** The dialog's field list for a page, with the shared wording. */
export function filterFieldsFor(page, fieldNames) {
  return fieldNames.map(
    (fieldName) => new FilterField(fieldName, fieldLabel(fieldName, page))
  )
}

/** Typed values (chips) and plain numbers, dates and Yes / No. */
export const SchemaBody = SchemaFilterBody

export const RoleFilterBody = schemaFilterBody({
  choices: Object.entries(AGENT_ROLES).map(([value, label]) => ({
    value,
    label,
  })),
})

export const PotentialFilterBody = schemaFilterBody({
  choices: POTENTIAL_TIER_FILTER_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label,
  })),
})

export const SourceFilterBody = schemaFilterBody({
  choices: TYPE_FILTER_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label,
  })),
})

export const LastCallOutcomeFilterBody = schemaFilterBody({
  choices: CALL_DISPOSITIONS,
})

/** My MLS: suggest the account's own MLSs; any typed name still works. */
export function MyMlsFilterBody(props) {
  const crmSettings = useSelector(selectCRMSettings)
  const mlss = crmSettings?.settings?.mlss
  const suggestions = useMemo(
    () => (mlss || []).map((mls) => mls.name).filter(Boolean),
    [mlss]
  )
  return <SchemaFilterBody {...props} suggestions={suggestions} />
}

export function StageFilterBody(props) {
  const { crmStages, isLoadingCRMStages, loadCRMStages } = useLoadCRMStages()

  useEffect(
    () => {
      loadCRMStages()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <SchemaFilterBody
      {...props}
      loadingChoices={isLoadingCRMStages}
      choices={(crmStages || []).map((stage) => ({
        value: stage.id,
        label: stage.name,
      }))}
    />
  )
}

export function CalledByFilterBody(props) {
  const axios = useAuthorizedAxiosClient()
  const [performers, setPerformers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    axios
      .get("/crm/api/activity/performers/")
      .then(({ data }) => {
        if (!cancelled) {
          setPerformers(Array.isArray(data) ? data : [])
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPerformers([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [axios])

  return (
    <SchemaFilterBody
      {...props}
      loadingChoices={loading}
      choices={performers.map((performer) => ({
        value: performer.id,
        label: performer.name || `User #${performer.id}`,
      }))}
    />
  )
}
